import React, { useState, useEffect } from 'react';
import { supportTicketService } from 'api/services/SupportTicketService';
import type { SupportTicketCategoryViewModel } from 'api/types/support-ticket';
import type { SupportTicketCreateModel } from 'api/types/support-ticket';

export interface SupportTicketFormProps {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

const SupportTicketForm: React.FC<SupportTicketFormProps> = ({ onSuccess, onError }) => {
  const [categories, setCategories] = useState<SupportTicketCategoryViewModel[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [formState, setFormState] = useState<SupportTicketCreateModel>({
    supportCategoryId: '',
    details: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingCategories(true);
    supportTicketService
      .getCategories()
      .then((res) => {
        if (cancelled) return;
        if (res.isSuccess && res.data) setCategories(Array.isArray(res.data) ? res.data : []);
      })
      .finally(() => {
        if (!cancelled) setLoadingCategories(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formState.supportCategoryId || !String(formState.details).trim()) {
      onError?.('Kategori ve açıklama alanları zorunludur.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await supportTicketService.create({
        supportCategoryId: formState.supportCategoryId,
        details: String(formState.details).trim(),
      });
      if (result.isSuccess) {
        setFormState({ supportCategoryId: '', details: '' });
        onSuccess?.();
      } else {
        onError?.(result.message ?? 'Talep oluşturulamadı.');
      }
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="sb-panel-card h-100">
      <div className="sb-panel-card__header">
        <h2 className="sb-panel-card__title">Yeni Destek Talebi</h2>
      </div>
      <div className="sb-panel-card__body">
        <div className="mb-3">
          <label className="form-label" htmlFor="support-category">
            Kategori
          </label>
          <select
            id="support-category"
            className="form-select"
            value={String(formState.supportCategoryId)}
            onChange={(e) => setFormState((s) => ({ ...s, supportCategoryId: e.target.value }))}
            required
            disabled={loadingCategories}
          >
            <option value="">Kategori seçin</option>
            {categories.map((c) => (
              <option key={String(c.id)} value={String(c.id)}>
                {c.name ?? c.id}
              </option>
            ))}
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label" htmlFor="support-details">
            Açıklama
          </label>
          <textarea
            id="support-details"
            className="form-control"
            rows={4}
            value={formState.details}
            onChange={(e) => setFormState((s) => ({ ...s, details: e.target.value }))}
            placeholder="Sorununuzu veya talebinizi kısaca açıklayın."
            required
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Gönderiliyor...
            </>
          ) : (
            'Gönder'
          )}
        </button>
      </div>
    </form>
  );
};

export default SupportTicketForm;
